import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Play, Mic, Calendar, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useCallback } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import nikitaVideo from "@/assets/nikita.mp4.asset.json";
import buccVideo from "@/assets/bucc-advertisement.mp4.asset.json";
import creditVideo from "@/assets/credit.mp4.asset.json";
import portfolioPlusVideo from "@/assets/portfolio-plus.mp4.asset.json";

// Initialize Cal.com
declare global {
  interface Window {
    Cal?: any;
  }
}

const initCalEmbed = () => {
  if (typeof window !== 'undefined' && !window.Cal) {
    (function (C: any, A: string, L: string) {
      let p = function (a: any, ar: any) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "premier-contact-accompagnement-strategique", { origin: "https://app.cal.com" });
    window.Cal.ns["premier-contact-accompagnement-strategique"]("ui", { "hideEventTypeDetails": false, "layout": "month_view" });
  }
};

const categories = [
  { id: "all", label: "Tous", icon: Play },
  { id: "Reels", label: "Reels", icon: Play },
  { id: "Podcasts", label: "Podcasts", icon: Mic },
  { id: "Événements", label: "Événements", icon: Calendar },
  { id: "Publicité", label: "Publicités", icon: Video },
];

// Featured videos carousel
const featuredVideos = [
  {
    id: 50,
    title: "Portfolio Plus",
    category: "all",
    description: "Un aperçu de nos réalisations.",
    videoUrl: portfolioPlusVideo.url,
  },
  {
    id: 10,
    title: "Luc Poirier X Les Inspirés de l'immobilier",
    category: "Événements",
    description: "Événement avec Luc Poirier pour Les Inspirés de l'immobilier.",
    youtubeId: "DBK_gulcMps",
  },
  {
    id: 18,
    title: "Publicité vidéo créative",
    category: "Publicité",
    description: "Publicité vidéo performante pour entreprises B2B.",
    youtubeId: "_JlqPf3yhKs",
  },
];

const portfolioItems = [
  {
    id: 40,
    title: "Nikita — Publicité",
    category: "Publicité",
    description: "Publicité vidéo créative.",
    videoUrl: nikitaVideo.url,
    isShort: true,
  },
  {
    id: 41,
    title: "Bureau canadien du crédit — Publicité",
    category: "Publicité",
    description: "Publicité vidéo pour le BUCC.",
    videoUrl: buccVideo.url,
    isShort: true,
  },
  {
    id: 42,
    title: "Crédit — Publicité",
    category: "Publicité",
    description: "Publicité vidéo sociale (ex-Facebook Reel).",
    videoUrl: creditVideo.url,
    isShort: true,
  },
  {
    id: 16,
    title: "Nouveau Reel",
    category: "Reels",
    categories: ["Reels", "Publicité"],
    description: "Reel créatif.",
    youtubeId: "tN6im51Vg_E",
    isShort: true,
  },
  {
    id: 24,
    title: "Reel organique",
    category: "Reels",
    description: "Contenu organique performant.",
    youtubeId: "8kK14DRXZOw",
    isShort: true,
  },
  {
    id: 6,
    title: "Le dernier conseil | Survivor",
    category: "Reels",
    description: "Contenu viral inspiré de Survivor.",
    youtubeId: "L5_MLbWHy8I",
  },
  {
    id: 4,
    title: "Alex G - Nascar promotion",
    category: "Publicité",
    categories: ["Publicité", "Reels"],
    description: "Promotion événementielle pour le pilote NASCAR Alex Guennette.",
    youtubeId: "v1pNW2JvEbk",
  },
  {
    id: 9,
    title: "Nouveau Reel",
    category: "Reels",
    description: "Reel créatif.",
    youtubeId: "3L0ZXFh0asI",
  },
  {
    id: 5,
    title: "Bureau canadien du crédit - Reel",
    category: "Reels",
    description: "Reel engageant pour le Bureau canadien du crédit.",
    youtubeId: "4EHvJZBzaIc",
  },
  {
    id: 7,
    title: "Groupe Sierra - Publicité chalet",
    category: "Publicité",
    description: "Publicité immobilière pour Groupe Sierra.",
    youtubeId: "oNcfRl_SU4c",
  },
  // ============ PODCASTS ============
  // Solemn Podcast
  {
    id: 30,
    title: "Solemn #47 - Jo Cormier : Faire de l'humour pour SOI-MÊME",
    category: "Podcasts",
    description: "Production podcast Solemn avec Jo Cormier.",
    youtubeId: "XQTcKllOHwg",
  },
  {
    id: 31,
    title: "Solemn - Dave Morgan : Les coulisses de Big Brother",
    category: "Podcasts",
    description: "Dave Morgan raconte les coulisses de Big Brother et son premier spectacle solo.",
    youtubeId: "M02RpdqOhF0",
  },
  {
    id: 32,
    title: "Solemn #45 - Alexandre Champagne : De Contrat de Gars au Stand-Up",
    category: "Podcasts",
    description: "La réinvention d'Alexandre Champagne.",
    youtubeId: "s4tNSuAjYm8",
  },
  // Collège MREX
  {
    id: 33,
    title: "Les Proprios #30 - Collège MREX",
    category: "Podcasts",
    description: "Production podcast pour Collège MREX.",
    youtubeId: "VveQzmW1wu4",
  },
  {
    id: 34,
    title: "MREX Épisode 30 - Investir avec ses valeurs",
    category: "Podcasts",
    description: "Discussion sur l'investissement immobilier aligné avec ses valeurs.",
    youtubeId: "RE6BnaxLvsM",
  },
  // Camions BL
  {
    id: 35,
    title: "Camions BL - La relation commis-client",
    category: "Podcasts",
    description: "Production podcast pour Camions BL.",
    youtubeId: "1W4w0XXbrgs",
  },
  {
    id: 36,
    title: "Camions BL - Daniel, co-fondateur",
    category: "Podcasts",
    description: "Daniel, co-fondateur de Camions BL, revient sur ce qui les définit.",
    youtubeId: "BkJgrVrlHI8",
  },
  // ============ PODCASTS existants (PMML + autres) ============
  {
    id: 19,
    title: "Entrepôts industriels - Les erreurs qui coûtent cher",
    category: "Podcasts",
    description: "Podcast sur les erreurs coûteuses dans l'immobilier industriel.",
    youtubeId: "oEpS-W9tRpc",
  },
  {
    id: 11,
    title: "Une authenticité sans compromis avec Dominic Paquet",
    category: "Podcasts",
    description: "Découvrez le parcours inspirant de Dominic Paquet.",
    youtubeId: "W5KF72dUzVQ",
  },
  {
    id: 20,
    title: "La transaction Norgate - 74 immeubles, 1108 logements",
    category: "Podcasts",
    description: "Discussion sur une transaction immobilière majeure.",
    youtubeId: "jgANL_902RA",
  },
  {
    id: 12,
    title: "Le derrière de l'absurde avec Jean-Thomas Jobin",
    category: "Podcasts",
    description: "Une conversation authentique avec Jean-Thomas Jobin.",
    youtubeId: "0WH7b5imWIA",
  },
  {
    id: 21,
    title: "Du terrain à l'impact collectif - L'approche Boda",
    category: "Podcasts",
    description: "Podcast avec Mélissa Jacob et Marie-Sara Hamel sur l'approche Boda.",
    youtubeId: "cEcttA6d6aA",
  },
  {
    id: 13,
    title: "From Mining to Real Estate: Joey Parent's Journey",
    category: "Podcasts",
    description: "Le parcours inspirant de Joey Parent en immobilier.",
    youtubeId: "niNdWhL092o",
  },
  {
    id: 22,
    title: "Leadership féminin et l'intelligence d'affaires",
    category: "Podcasts",
    description: "Discussion sur le leadership féminin dans l'immobilier commercial.",
    youtubeId: "wW6u54nI87w",
  },
  {
    id: 14,
    title: "Quand l'entrepreneuriat et l'art se rencontrent",
    category: "Podcasts",
    description: "Le parcours inspirant d'Olivier Corneau.",
    youtubeId: "Ec3hj028qvY",
  },
  {
    id: 23,
    title: "J'ai acheté des immeubles à prix réduit",
    category: "Podcasts",
    description: "Stratégies d'achat immobilier à prix réduit.",
    youtubeId: "B3NGfkwTlto",
  },
];

const PortfolioPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    initCalEmbed();
  }, []);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };

    carouselApi.on("select", onSelect);
    onSelect();

    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  const filteredItems = activeCategory === "all" 
    ? portfolioItems 
    : portfolioItems.filter(item => 
        item.categories ? item.categories.includes(activeCategory) : item.category === activeCategory
      );

  // Filter featured videos based on active category
  const filteredFeaturedVideos = activeCategory === "all"
    ? featuredVideos
    : featuredVideos.filter(video => video.category === activeCategory || video.category === "all");

  return (
    <>
      <SEOHead
        title="Portfolio | Réalisations et études de cas – Agence 8020"
        description="Découvrez les projets et études de cas de l'Agence 8020 en création de contenu vidéo et marketing B2B."
        canonical="/portfolio"
      />
      <main className="min-h-screen bg-background">
        <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] gradient-glow animate-pulse-glow" />
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold mb-6"
          >
            <span className="text-primary">Portfolio</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto"
          >
            Des idées bien exécutées. Du contenu qui fait réagir. Voici quelques mandats 
            qu'on a réalisés, allant de la publicité numérique à la création de contenu organique.
          </motion.p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-2 md:gap-3"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`relative flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-3 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeCategory === category.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary/50 text-muted-foreground hover:text-foreground border border-border hover:border-primary/30"
                }`}
              >
                <category.icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>{category.label}</span>
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Videos Carousel */}
      {filteredFeaturedVideos.length > 0 && (
        <section className="py-8">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="max-w-4xl mx-auto relative"
            >
              {filteredFeaturedVideos.length === 1 ? (
                // Single video - no carousel needed
                <div className="rounded-2xl bg-card border border-primary/50 overflow-hidden shadow-2xl">
                  <div className="relative overflow-hidden bg-black" style={{ aspectRatio: "16/9" }}>
                    {filteredFeaturedVideos[0].videoUrl ? (
                      <video
                        src={filteredFeaturedVideos[0].videoUrl}
                        controls
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <iframe
                        src={`https://www.youtube.com/embed/${filteredFeaturedVideos[0].youtubeId}`}
                        title={filteredFeaturedVideos[0].title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      />
                    )}
                  </div>
                  <div className="p-4 md:p-6">
                    <span className="inline-block bg-primary/10 text-primary text-xs md:text-sm font-medium px-2 md:px-3 py-1 rounded-full mb-2 md:mb-3">
                      {filteredFeaturedVideos[0].category}
                    </span>
                    <h3 className="font-heading text-base md:text-xl lg:text-2xl font-bold text-foreground mb-1 md:mb-2">
                      {filteredFeaturedVideos[0].title}
                    </h3>
                    <p className="text-muted-foreground text-sm md:text-base">{filteredFeaturedVideos[0].description}</p>
                  </div>
                </div>
              ) : (
                // Multiple videos - use carousel
                <>
                  <Carousel 
                    className="w-full overflow-visible" 
                    setApi={setCarouselApi}
                    opts={{
                      align: "center",
                      loop: true,
                    }}
                  >
                    <CarouselContent className="-ml-4 overflow-visible">
                      {filteredFeaturedVideos.map((video, index) => (
                        <CarouselItem 
                          key={video.id} 
                          className="pl-4 basis-[80%] sm:basis-[75%] md:basis-[70%] transition-all duration-500"
                        >
                          <div 
                            className={`rounded-2xl bg-card border overflow-hidden transition-all duration-500 ${
                              currentSlide === index 
                                ? "border-primary/50 scale-100 opacity-100 shadow-2xl relative z-10" 
                                : "border-border scale-[0.85] opacity-50"
                            }`}
                          >
                            <div className="relative overflow-hidden bg-black" style={{ aspectRatio: "16/9" }}>
                              {video.videoUrl ? (
                                <video
                                  src={video.videoUrl}
                                  controls
                                  playsInline
                                  preload="metadata"
                                  className="absolute inset-0 w-full h-full object-cover"
                                />
                              ) : (
                                <iframe
                                  src={`https://www.youtube.com/embed/${video.youtubeId}`}
                                  title={video.title}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  className="absolute inset-0 w-full h-full"
                                />
                              )}
                            </div>
                            {video.category !== "all" && (
                              <div className="p-4 md:p-6">
                                <span className="inline-block bg-primary/10 text-primary text-xs md:text-sm font-medium px-2 md:px-3 py-1 rounded-full mb-2 md:mb-3">
                                  {video.category}
                                </span>
                                <h3 className="font-heading text-base md:text-xl lg:text-2xl font-bold text-foreground mb-1 md:mb-2">
                                  {video.title}
                                </h3>
                                <p className="text-muted-foreground text-sm md:text-base line-clamp-2">{video.description}</p>
                              </div>
                            )}
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="left-0 md:-left-12 h-8 w-8 md:h-10 md:w-10" />
                    <CarouselNext className="right-0 md:-right-12 h-8 w-8 md:h-10 md:w-10" />
                  </Carousel>
                  {/* Dots indicator */}
                  <div className="flex justify-center gap-3 mt-6">
                    {filteredFeaturedVideos.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => carouselApi?.scrollTo(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          currentSlide === index 
                            ? "bg-primary scale-125" 
                            : "bg-primary/30 hover:bg-primary/50"
                        }`}
                        aria-label={`Aller à la vidéo ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Portfolio Grid - Reel Format */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className={`grid gap-4 md:gap-6 ${
                activeCategory === "Podcasts" 
                  ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto" 
                  : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
              }`}
            >
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group"
                >
                  <div className="rounded-2xl bg-card border border-border overflow-hidden hover:border-primary/50 hover-glow transition-all duration-500">
                    {/* Video Embed */}
                    <div 
                      className="relative overflow-hidden bg-black" 
                      style={{ aspectRatio: item.category === "Podcasts" ? "16/9" : "9/16" }}
                    >
                      {item.videoUrl ? (
                        <video
                          src={item.videoUrl}
                          controls
                          playsInline
                          preload="metadata"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <iframe
                          src={`https://www.youtube.com/embed/${item.youtubeId}${item.isShort ? '?loop=1' : ''}`}
                          title={item.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 w-full h-full"
                        />
                      )}
                    </div>
                    
                    {item.category !== "Publicité" && (
                      <div className="p-4">
                        <span className="text-primary text-xs font-medium">{item.category}</span>
                        <h3 className="font-heading text-sm md:text-base font-bold text-foreground mt-1 line-clamp-2">
                          {item.title}
                        </h3>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {filteredItems.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="text-muted-foreground text-lg">Aucun projet dans cette catégorie pour le moment.</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-card/50">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              <span className="text-foreground">Prêt à créer </span>
              <span className="text-primary">votre projet?</span>
            </h2>
            <Button 
              variant="hero" 
              size="xl" 
              className="group"
              data-cal-link="maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique"
              data-cal-namespace="premier-contact-accompagnement-strategique"
              data-cal-config='{"layout":"month_view"}'
            >
              Réserver un appel découverte
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
      </main>
    </>
  );
};

export default PortfolioPage;
